/**
 * I didn't write this thing (way too advanced for me ;)).
 * I borrowed it from Patrick (inlet.nl).
 */
import * as React from 'react';
import {
    ComponentProps,
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useState,
} from 'react';

import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';

type ConfirmValue = {
    title: ReactNode;
    body: ReactNode;
    resolve: (value: boolean | PromiseLike<boolean>) => void;
    customRender?: (props: ConfirmModalProps) => ReactNode;
};

type ConfirmModalProps = {
    value: ConfirmValue;
    onCancel: () => void;
    onSubmit: () => void;
    context: ComponentProps<typeof ConfirmationDialog>['context'];
};

const ConfirmModal = ({
    value,
    onCancel,
    onSubmit,
    context,
}: ConfirmModalProps) => (
    <ConfirmationDialog
        onConfirm={onSubmit}
        onCancel={onCancel}
        title={value.title}
        context={context}
    >
        {value.body}
    </ConfirmationDialog>
);

type ConfirmFunction = {
    (
        title: ConfirmValue['title'],
        body: ConfirmValue['body'],
        customRender?: (props: ConfirmModalProps) => ReactNode
    ): PromiseLike<boolean>;
};

export const ConfirmContext = createContext<ConfirmFunction>(() =>
    Promise.resolve(true)
);

type ConfirmDialogProviderProps = {
    children: ReactNode;
    context: ComponentProps<typeof ConfirmationDialog>['context'];
};

export const ConfirmDialogProvider = ({
    children,
    context,
}: ConfirmDialogProviderProps) => {
    const [value, setValue] = useState<ConfirmValue>();

    const confirm = useCallback<ConfirmFunction>(
        async (title, body, customRender) =>
            new Promise((resolve) =>
                setValue({ title, body, resolve, customRender })
            ),
        [setValue]
    );

    const onCancel = useCallback(() => {
        value?.resolve(false);
        setValue(undefined);
    }, [value && value.resolve]);

    const onSubmit = useCallback(() => {
        value?.resolve(true);
        setValue(undefined);
    }, [value && value.resolve]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}

            {value &&
                (value.customRender ? (
                    value.customRender({ value, onCancel, onSubmit, context })
                ) : (
                    <ConfirmModal
                        value={value}
                        onCancel={onCancel}
                        onSubmit={onSubmit}
                        context={context}
                    />
                ))}
        </ConfirmContext.Provider>
    );
};

export const useConfirmationDialog = () => useContext(ConfirmContext);
